import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ChevronRight } from 'lucide-react';

export function NavItem({
    redirectUrl,
    Icon,
    label,
    badge,
    isActive,
    isCollapsed,
    hasSubItems,
    isExpanded,
    onToggle,
    subItems = [],
    children
}) {
    const history = useHistory();
    const location = useLocation();
    const buttonRef = useRef(null);
    const timeoutRef = useRef(null);
    const tooltipRef = useRef(null);
    const bridgeRef = useRef(null);

    const [isHovered, setIsHovered] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState(null);
    const active = isActive !== undefined ? isActive : (redirectUrl && location.pathname === redirectUrl);

    const updateTooltipPosition = useCallback(() => {
        if (!isCollapsed || !isHovered || !buttonRef.current || !tooltipRef.current) {
            return;
        }

        const buttonRect = buttonRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const gap = 26;
        const margin = 12;

        let top = buttonRect.top + (buttonRect.height / 2) - (tooltipRect.height / 2);
        top = Math.max(margin, Math.min(top, viewportHeight - tooltipRect.height - margin));

        let left = buttonRect.right + gap;
        left = Math.min(left, viewportWidth - tooltipRect.width - margin);

        const arrowCenter = buttonRect.top + (buttonRect.height / 2) - top;
        const arrowTop = Math.max(14, Math.min(arrowCenter, tooltipRect.height - 14));

        setTooltipPosition({
            left: `${left}px`,
            top: `${top}px`,
            arrowTop: `${arrowTop}px`,
        });
    }, [isCollapsed, isHovered]);

    useEffect(() => {
        if (!isCollapsed || !isHovered) {
            setTooltipPosition(null);
            return undefined;
        }

        const frameId = window.requestAnimationFrame(updateTooltipPosition);
        const handleViewportChange = () => updateTooltipPosition();

        window.addEventListener("resize", handleViewportChange);
        window.addEventListener("scroll", handleViewportChange, true);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener("resize", handleViewportChange);
            window.removeEventListener("scroll", handleViewportChange, true);
        };
    }, [isCollapsed, isHovered, updateTooltipPosition]);

    useEffect(() => {
        if (!isCollapsed || !isHovered || !buttonRef.current) {
            return undefined;
        }

        const isPointWithinRect = (rect, clientX, clientY) => (
            rect
            && clientX >= rect.left
            && clientX <= rect.right
            && clientY >= rect.top
            && clientY <= rect.bottom
        );

        const handlePointerMove = (event) => {
            const { clientX, clientY } = event;
            const buttonRect = buttonRef.current?.getBoundingClientRect();

            if (isPointWithinRect(buttonRect, clientX, clientY)) {
                return;
            }

            if (hasSubItems) {
                const bridgeRect = bridgeRef.current?.getBoundingClientRect();
                const tooltipRect = tooltipRef.current?.getBoundingClientRect();

                if (
                    isPointWithinRect(bridgeRect, clientX, clientY)
                    || isPointWithinRect(tooltipRect, clientX, clientY)
                ) {
                    return;
                }
            }

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            setIsHovered(false);
        };

        window.addEventListener("mousemove", handlePointerMove);

        return () => {
            window.removeEventListener("mousemove", handlePointerMove);
        };
    }, [hasSubItems, isCollapsed, isHovered]);

    useEffect(() => () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    }, []);

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (isCollapsed) {
            setIsHovered(true);
        }
    };

    const handleMouseLeave = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        const delay = hasSubItems ? 150 : 50;
        timeoutRef.current = setTimeout(() => setIsHovered(false), delay);
    };

    const handleTooltipEnter = () => {
        if (hasSubItems && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            setIsHovered(true);
        }
    };

    const handleTooltipLeave = () => {
        if (hasSubItems) {
            handleMouseLeave();
        }
    };

    return (
        <li 
            className="nav-item-wrapper"
        >
            <button
                ref={buttonRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={(e) => {
                    if (hasSubItems) {
                        e.preventDefault();
                        if (onToggle) onToggle();
                    } else if (redirectUrl) {
                        history.push(redirectUrl);
                    }
                }}
                className={`nav-item-btn ${active ? 'active' : ''} ${isCollapsed ? 'collapsed-mode' : ''}`}
            >
                {active && !isCollapsed && <div className="active-indicator" />}
                {Icon && (
                    <span className="nav-icon-slot">
                        <Icon className="nav-icon" />
                    </span>
                )}
                <span className="nav-label">{label}</span>
                {badge && <span className="badge">{badge}</span>}
                {hasSubItems && <ChevronRight className={`arrow-icon ${isExpanded ? 'rotated' : ''}`} />}
            </button>
            {isCollapsed && isHovered && (
                <>
                    {hasSubItems && (
                        <div
                            ref={bridgeRef}
                            className="tooltip-bridge"
                            onMouseEnter={handleTooltipEnter}
                            onMouseLeave={handleTooltipLeave}
                        />
                    )}

                    <div 
                        ref={tooltipRef}
                        className={`sidebar-floating-tooltip ${hasSubItems ? 'has-menu' : 'simple-tip'}`}
                        onMouseEnter={handleTooltipEnter}
                        onMouseLeave={handleTooltipLeave}
                        style={tooltipPosition ? {
                            left: tooltipPosition.left,
                            top: tooltipPosition.top,
                            ["--tooltip-arrow-top"]: tooltipPosition.arrowTop,
                        } : undefined}
                    >
                        <div className="tooltip-header">
                            {label}
                            {badge && <span className="tooltip-badge">{badge}</span>}
                        </div>

                        {hasSubItems && subItems.length > 0 && (
                            <ul className="tooltip-submenu">
                                {subItems.map((sub) => (
                                    <li key={sub.id}>
                                        <button 
                                            onClick={() => {
                                                history.push(sub.url);
                                                setIsHovered(false);
                                            }}
                                            className={location.pathname === sub.url ? 'active' : ''}
                                        >
                                            {sub.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="tooltip-arrow" />
                    </div>
                </>
            )}
            {!isCollapsed && children}
        </li>
    );
}
